import { useState, useEffect, useRef } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';
import { formatEuros, centsToInput, inputToCents } from '../lib/format.js';

// Pakketbeheer van één product: de verkooptekst plus de pakketten met hun
// gebruikersplafond en prijzen. Het bovenste pakket hoort geen plafond te
// hebben, zodat er altijd iets is waar een groeiende klant in past.
export default function ProductPlansModal({ isOpen, product, onClose, onSaved }) {
  const [description, setDescription] = useState('');
  const [descriptionBusy, setDescriptionBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPlan());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Why: een fout bij Verwijderen hoort bij de lijst waar je op klikte, niet
  // onderaan bij het formulier waar hij buiten beeld valt.
  const [listError, setListError] = useState(null);
  // Het product dat nu open staat, zodat een late fout van een verwijdering
  // niet in het venster van een ander product belandt.
  const openProductId = useRef(null);
  openProductId.current = isOpen ? product?.id : null;

  const plans = product?.plans || [];
  const hasOpenEnded = plans.some(p => p.maxUsers == null);

  // Why: alleen op het id. Het product komt na elke ophaalronde (elke 10 s)
  // als nieuw object binnen; daarop resetten zou een half ingevuld formulier
  // wissen.
  useEffect(() => {
    if (!isOpen || !product) return;
    setDescription(product.description || '');
    setEditing(null);
    setForm(emptyPlan());
    setError(null);
    setListError(null);
  }, [isOpen, product?.id]);

  function set(field, value) {
    if (error) setError(null);
    setForm(f => ({ ...f, [field]: value }));
  }

  function startEdit(plan) {
    setError(null);
    setListError(null);
    setEditing(plan);
    setForm({
      name: plan.name,
      maxUsers: plan.maxUsers == null ? '' : String(plan.maxUsers),
      monthlyEur: centsToInput(plan.monthlyPriceCents),
      yearlyEur: centsToInput(plan.yearlyPriceCents),
      extraMonthlyEur: centsToInput(plan.extraDepartmentMonthlyCents),
      extraYearlyEur: centsToInput(plan.extraDepartmentYearlyCents),
      isSellable: plan.isSellable
    });
  }

  function cancelEdit() {
    setEditing(null);
    setForm(emptyPlan());
    setError(null);
  }

  async function submitPlan(e) {
    e.preventDefault();
    setError(null);
    setListError(null);
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        // Leeg plafond betekent: dit is het bovenste pakket, alles past erin.
        max_users: form.maxUsers === '' ? null : Number.parseInt(form.maxUsers, 10),
        monthly_price_cents: inputToCents(form.monthlyEur),
        yearly_price_cents: inputToCents(form.yearlyEur),
        // Leeg = met dit pakket kan een club maar één afdeling hebben
        extra_department_monthly_cents: inputToCents(form.extraMonthlyEur),
        extra_department_yearly_cents: inputToCents(form.extraYearlyEur),
        is_sellable: form.isSellable
      };
      const url = editing
        ? `/api/products/${product.id}/plans/${editing.id}`
        : `/api/products/${product.id}/plans`;
      const res = await fetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || 'save_failed');
      cancelEdit();
      onSaved();
    } catch (err) {
      setError(translate(err.message));
    } finally {
      setBusy(false);
    }
  }

  async function removePlan(plan) {
    if (!confirm(`Pakket "${plan.name}" verwijderen?`)) return;
    const productId = product.id;
    const fail = code => { if (openProductId.current === productId) setListError(translate(code)); };
    setListError(null);
    try {
      const res = await fetch(`/api/products/${productId}/plans/${plan.id}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      });
      if (res.ok) {
        if (editing?.id === plan.id) cancelEdit();
        return onSaved();
      }
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      fail(data.error || 'delete_failed');
    } catch {
      fail('delete_failed');
    }
  }

  async function saveDescription() {
    setDescriptionBusy(true);
    try {
      await fetch(`/api/products/${product.id}/description`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ description: description.trim() || null })
      });
      onSaved();
    } finally {
      setDescriptionBusy(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="wide" title={`Pakketten: ${product?.name || ''}`}>
      <div class="plans-manager">
        <label class="plans-description">
          <span>Verkooptekst van het product</span>
          <textarea
            value={description}
            onInput={e => setDescription(e.currentTarget.value)}
            rows={2}
            maxLength={2000}
            placeholder="Wat doet dit product?"
          />
          <Button size="sm" variant="ghost" type="button" onClick={saveDescription} disabled={descriptionBusy}>
            {descriptionBusy ? 'Opslaan…' : 'Tekst opslaan'}
          </Button>
        </label>

        <PlanTable plans={plans} error={listError} onEdit={startEdit} onRemove={removePlan} />

        {plans.length > 0 && !hasOpenEnded && (
          <p class="form-warning">
            Geen enkel pakket is onbeperkt. Een klant die boven het hoogste plafond
            groeit past dan nergens meer in — laat het gebruikersplafond leeg bij je
            zwaarste pakket.
          </p>
        )}

        <form onSubmit={submitPlan} class="app-form plans-form">
          <h4>{editing ? `Pakket bewerken: ${editing.name}` : 'Pakket toevoegen'}</h4>
          <div class="form-row-pair">
            <label style={{ flex: 2 }}>
              <span>Naam</span>
              <input
                value={form.name}
                onInput={e => set('name', e.currentTarget.value)}
                required
                maxLength={60}
                placeholder="Basis"
              />
            </label>
            <label>
              <span>Tot en met … gebruikers</span>
              <input
                type="number" min="1" step="1"
                value={form.maxUsers}
                onInput={e => set('maxUsers', e.currentTarget.value)}
                placeholder="leeg = onbeperkt"
              />
            </label>
          </div>
          <div class="form-row-pair">
            <label>
              <span>Per maand (€, excl. BTW)</span>
              <input
                type="number" min="0" step="0.01"
                value={form.monthlyEur}
                onInput={e => set('monthlyEur', e.currentTarget.value)}
                placeholder="39.00"
              />
            </label>
            <label>
              <span>Per jaar (€, excl. BTW)</span>
              <input
                type="number" min="0" step="0.01"
                value={form.yearlyEur}
                onInput={e => set('yearlyEur', e.currentTarget.value)}
                placeholder="390.00"
              />
            </label>
          </div>
          <div class="form-row-pair">
            <label>
              <span>Extra afdeling per maand (€, excl. BTW)</span>
              <input
                type="number" min="0" step="0.01"
                value={form.extraMonthlyEur}
                onInput={e => set('extraMonthlyEur', e.currentTarget.value)}
                placeholder="leeg = één afdeling"
              />
            </label>
            <label>
              <span>Extra afdeling per jaar (€, excl. BTW)</span>
              <input
                type="number" min="0" step="0.01"
                value={form.extraYearlyEur}
                onInput={e => set('extraYearlyEur', e.currentTarget.value)}
                placeholder="leeg = één afdeling"
              />
            </label>
          </div>
          <p class="muted small">Toeslag per afdeling boven de eerste (omnivereniging). Laat leeg als dit pakket maar één afdeling toestaat.</p>
          <label class="checkbox-row">
            <input
              type="checkbox"
              checked={form.isSellable}
              onInput={e => set('isSellable', e.currentTarget.checked)}
            />
            <span>Verkoopbaar — toon op de publieke aanvraagpagina</span>
          </label>
          <p class="muted small">Zonder beide prijzen wordt verkoopbaar automatisch uitgezet.</p>

          {error && <div class="form-error">{error}</div>}
          <div class="form-actions">
            {editing && <Button variant="ghost" type="button" onClick={cancelEdit}>Annuleren</Button>}
            <Button type="submit" disabled={busy}>
              {busy ? 'Bezig…' : (editing ? 'Opslaan' : 'Toevoegen')}
            </Button>
          </div>
        </form>

        <div class="form-actions">
          <Button variant="ghost" type="button" onClick={onClose}>Sluiten</Button>
        </div>
      </div>
    </Modal>
  );
}

// De pakketlijst met de fout van Verwijderen er direct onder.
function PlanTable({ plans, error, onEdit, onRemove }) {
  return (
    <div class="plans-list">
      {plans.length === 0 ? (
        <p class="muted small">Nog geen pakketten. Voeg er hieronder één toe.</p>
      ) : (
        <div class="table-scroll">
        <table class="data-table">
          <thead>
            <tr><th>Pakket</th><th>Tot</th><th>Per maand</th><th>Per jaar</th><th>Extra afdeling</th><th /></tr>
          </thead>
          <tbody>
            {plans.map(plan => (
              <tr key={plan.id} class={plan.isSellable ? '' : 'row-muted'}>
                <td>
                  {plan.name}
                  {!plan.isSellable && <span class="muted small"> · niet verkoopbaar</span>}
                </td>
                <td class="muted">{plan.maxUsers == null ? 'onbeperkt' : `${plan.maxUsers} gebruikers`}</td>
                <td>{plan.monthlyPriceCents == null ? '—' : formatEuros(plan.monthlyPriceCents)}</td>
                <td>{plan.yearlyPriceCents == null ? '—' : formatEuros(plan.yearlyPriceCents)}</td>
                <td class="muted">{plan.extraDepartmentMonthlyCents == null && plan.extraDepartmentYearlyCents == null ? '—'
                  : `${plan.extraDepartmentMonthlyCents == null ? '—' : formatEuros(plan.extraDepartmentMonthlyCents)} p/m · ${plan.extraDepartmentYearlyCents == null ? '—' : formatEuros(plan.extraDepartmentYearlyCents)} p/j`}</td>
                <td class="row-actions">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(plan)}>Bewerken</Button>
                  <Button size="sm" variant="ghost" onClick={() => onRemove(plan)}>Verwijderen</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {error && <div class="form-error" role="alert">{error}</div>}
    </div>
  );
}

function emptyPlan() {
  return { name: '', maxUsers: '', monthlyEur: '', yearlyEur: '', extraMonthlyEur: '', extraYearlyEur: '', isSellable: true };
}

function translate(code) {
  const messages = {
    invalid_input: 'Controleer de naam, het plafond en de prijzen.',
    plan_name_in_use: 'Er bestaat al een pakket met deze naam bij dit product.',
    plan_in_use: 'Dit pakket heeft nog lopende abonnementen — zet die eerst over.',
    not_found: 'Pakket niet meer gevonden — herlaad de pagina.',
    save_failed: 'Opslaan is mislukt.',
    delete_failed: 'Verwijderen is mislukt.'
  };
  return messages[code] || 'Er ging iets mis. Probeer opnieuw.';
}
